import * as Sentry from '@sentry/browser'
import { object, ref, string } from 'yup'

import { ActionType, PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import trackEvent from 'utils/analytics/trackEvent'
import { sendToSentry, transformErrors } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { parseGraphQLError } from 'utils/form-utils'
import { getGeneralError } from 'utils/getGeneralError/getGeneralError'
import defaults from 'utils/object/defaults'
import { routingMask } from 'utils/text-masks/routing'

import { generateErrorCodeMap } from './errorCodeMap'

const SPECIFIC_ERROR_TO_HANDLE = ['institution_not_supported', 'invalid_account_number']

export type ACHProps = {
  routingNumber: string
  accountNumber: string
  confirmAccountNumber: string
}

// Validation messages
const routingNumberError = 'Routing number must consist of 9 digits.'
const accountNumberError = 'The account number is not valid.'
const confirmAccountNumberError = 'Account numbers do not match.'

export const validationSchema = object().shape({
  routingNumber: string()
    .required(routingNumberError)
    .min(9, routingNumberError)
    .max(9, routingNumberError)
    .matches(/^[0-9]{9}$/, routingNumberError),
  accountNumber: string()
    .required(accountNumberError)
    .matches(/^[0-9]*$/, accountNumberError),
  confirmAccountNumber: string()
    .oneOf([ref('accountNumber')], confirmAccountNumberError)
    .required(confirmAccountNumberError),
})

// Handles Formik's validate event.
export const validate = values => {
  let errors = {}

  try {
    validationSchema.validateSync(values, { abortEarly: false })
  } catch (validationErrors) {
    // Convert validation errors to Formik errors collection
    errors = transformErrors(validationErrors.inner)
  }

  return errors
}

export const mapPropsToValues = props => {
  const values = defaults(
    {
      routingNumber: '',
      accountNumber: '',
      confirmAccountNumber: '',
    },
    props.partner && props.partner.routingNumbers.length
      ? { routingNumber: props.partner.routingNumbers[0].routingNumber }
      : {},
  )

  return {
    ...values,
  }
}

export default partnerData => [
  [
    {
      label:
        partnerData && partnerData.routingNumbers.length
          ? partnerData.org.name + ' Routing Number'
          : 'Routing Number',
      name: 'routingNumber',
      type: 'tel',
      placeholder: '000000000',
      inputMode: 'numeric',
      autocomplete: 'ÑöCompléte',
      disabled: partnerData && partnerData.routingNumbers.length,
      mask: routingMask,
      sizes: { xs: 12, sm: 12, md: 12, lg: 12 },
      guide: false,
      id: TestIDs.MANUAL_FUNDING_SOURCE.ROUTING_NUMBER,
    },
  ],
  [
    {
      label: 'Account Number',
      name: 'accountNumber',
      type: 'text',
      placeholder: '000000000',
      inputMode: 'numeric',
      autocomplete: 'ÑöCompléte',
      sizes: { xs: 12, sm: 12, md: 12, lg: 12 },
      guide: false,
      id: TestIDs.MANUAL_FUNDING_SOURCE.ACCOUNT_NUMBER,
    },
  ],
  [
    {
      label: 'Confirm Account Number',
      name: 'confirmAccountNumber',
      type: 'text',
      placeholder: '000000000',
      inputMode: 'numeric',
      autocomplete: 'ÑöCompléte',
      noPaste: true,
      sizes: { xs: 12, sm: 12, md: 12, lg: 12 },
      guide: false,
      id: TestIDs.MANUAL_FUNDING_SOURCE.CONFIRM_ACCOUNT_NUMBER,
    },
  ],
]

export const handleSubmit = (values, { props, setFieldError, ...ownProps }) => {
  Sentry.addBreadcrumb({
    message: 'User is attempting to manually link an ACH account',
  })
  ownProps.setSubmitting(true)

  const variables = {
    routing: values.routingNumber,
    account: values.accountNumber,
  }

  return props
    .linkACHAccount({
      variables,
    })
    .then(() => {
      const financialInstitution = props.location?.state?.financialInstitution
      const linkedSubaccounts = [
        {
          name: 'Checking Account',
          accountNumberLastFour: variables.account.slice(-4),
        },
      ]

      trackEvent('ACH:Success', { location, partner: props.partner, flow: props.flow })
      props.trackOptimizely('ACH_success')
      props.trackOptimizely('banklinking.complete')
      const locationState = { linkedSubaccounts, financialInstitution, isManualFundingSource: true }
      props.isDynamicFunnelEnabled
        ? props.routeToPage(PageName.ManualFundingSource, ActionType.Continue, locationState)
        : props.route({ from: FlowRoute.ManualFundingSource, action: 'continue', locationState })
    })
    .catch(error => {
      ownProps.setSubmitting(false)
      const errorCode = parseGraphQLError(error)
      const errorCodeMap = generateErrorCodeMap(props, values)

      if (errorCode && errorCodeMap.get(errorCode)) {
        props.closeErrorModal()
        // handle rendering special errors on formik validation fields
        if (SPECIFIC_ERROR_TO_HANDLE.includes(errorCode)) {
          const { field, message } = errorCodeMap.get(errorCode)[0]

          setFieldError(field, message)
          trackEvent('Container Viewed', {
            eventData: {
              object_name: 'registration.error',
              type: 'container_viewed',
              event_text: 'Container Viewed',
              screen: 'bankLinkingError',
              error: errorCode,
            },
          })
        }
      } else {
        const retryHandler = () => {
          props.closeErrorModal()
          ownProps.submitForm()
        }
        const { title, message, actions, image } = getGeneralError(error, props, retryHandler)
        props.showErrorModal({ title, message, actions, image })
      }

      trackEvent('ACH:Fail', { location, partner: props.partner, flow: props.flow })
      sendToSentry(error)
    })
}
